import { Plural, Trans } from '@lingui/react/macro';
import { CircleCheck, TriangleAlert } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { CollisionsList } from '@/components/CollisionsList';
import { SimulationMeshCard } from '@/components/SimulationMeshCard';
import { SimulationVideoCard } from '@/components/SimulationVideoCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { CollisionInfo, ProjectDetail } from '@/types/api';

export const SimulationResultsSection = ({ project }: { project: ProjectDetail }): React.ReactElement => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentVideoTime, setCurrentVideoTime] = useState<number | undefined>(undefined);

  const collisionsWithFiles = useMemo(() => {
    const result: Array<{ collision: CollisionInfo; fileName: string }> = [];
    for (const file of project.program_files) {
      for (const collision of file.collisions ?? []) {
        result.push({ collision, fileName: file.original_filename });
      }
    }
    return result.sort((a, b) => b.collision.depth - a.collision.depth);
  }, [project.program_files]);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_380px]">
      <div className="flex flex-col gap-4">
        <SimulationMeshCard projectId={project.id} />
        <SimulationVideoCard projectId={project.id} videoRef={videoRef} onTimeUpdate={setCurrentVideoTime} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <Trans>Simulation results</Trans>
          </CardTitle>
          {project.simulation_state === 'complete_collisions' ? (
            <CardDescription className="flex items-center gap-2">
              <TriangleAlert className="size-4 text-red-600" />
              <span>
                <Plural value={collisionsWithFiles.length} one="# collision detected" other="# collisions detected" />
              </span>
            </CardDescription>
          ) : undefined}
        </CardHeader>
        <CardContent className="min-h-0 flex-1 overflow-y-auto">
          {project.simulation_state === 'complete_ok' ? (
            <div className="mt-12 flex flex-col items-center justify-center font-semibold text-green-700 gap-4">
              <CircleCheck size={48} />
              <span className="text-xl">
                <Trans>No collisions</Trans>
              </span>
            </div>
          ) : (
            <CollisionsList
              collisions={collisionsWithFiles}
              onJumpToTime={(s) => {
                if (videoRef.current) videoRef.current.currentTime = s;
              }}
              currentVideoTime={currentVideoTime}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};
